import { useParams, Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase/config";

const REFRESH_INTERVAL_MS = 15000;
const MAX_EVENTS = 10;

const getFirstName = (fullName) => {
  const value = String(fullName || "").trim();
  if (!value) return "";
  if (value.includes(",")) {
    const parts = value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length >= 2) return parts[1].split(" ")[0] || parts[1];
    return parts[0];
  }
  return value.split(" ")[0] || value;
};

export default function StudentReminders() {
  const { id } = useParams();
  const [className, setClassName] = useState("");
  const [studentMap, setStudentMap] = useState({});
  const [positives, setPositives] = useState([]);
  const [negatives, setNegatives] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionStart, setSessionStart] = useState(() => new Date());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const classSnap = await getDoc(doc(db, "classes", id));
      if (classSnap.exists()) {
        setClassName(classSnap.data().name || "");
      }

      const studentsRef = collection(db, "classes", id, "students");
      const studentsSnap = await getDocs(studentsRef);
      const nextMap = {};
      studentsSnap.forEach((docSnap) => {
        nextMap[docSnap.id] = docSnap.data().name || docSnap.id;
      });
      setStudentMap(nextMap);

      const eventsRef = collection(db, "classes", id, "events");
      const sessionStartTimestamp = Timestamp.fromDate(sessionStart);
      const positivesQuery = query(
        eventsRef,
        where("type", "==", "positive"),
        where("timestamp", ">=", sessionStartTimestamp),
        orderBy("timestamp", "desc"),
        limit(MAX_EVENTS)
      );
      const negativesQuery = query(
        eventsRef,
        where("type", "==", "negative"),
        where("timestamp", ">=", sessionStartTimestamp),
        orderBy("timestamp", "desc"),
        limit(MAX_EVENTS)
      );

      const [positivesSnap, negativesSnap] = await Promise.all([
        getDocs(positivesQuery),
        getDocs(negativesQuery)
      ]);

      setPositives(positivesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setNegatives(negativesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [id, sessionStart]);

  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadData]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const clearView = () => {
    setSessionStart(new Date());
    setPositives([]);
    setNegatives([]);
    setLastUpdated(null);
  };

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }

    await document.documentElement.requestFullscreen();
  };

  const aggregateEvents = useCallback((events) => {
    const map = new Map();
    events.forEach((event) => {
      const studentId = Array.isArray(event.studentIds) ? event.studentIds[0] : null;
      if (!studentId) return;
      const points = Number(event.points) || 1;
      const existing = map.get(studentId) || 0;
      map.set(studentId, existing + points);
    });
    return Array.from(map.entries()).map(([studentId, points]) => ({
      studentId,
      points,
    }));
  }, []);

  const positiveRows = useMemo(() => aggregateEvents(positives), [aggregateEvents, positives]);
  const negativeRows = useMemo(() => aggregateEvents(negatives), [aggregateEvents, negatives]);

  const renderRow = (row, variant) => {
    const studentName = studentMap[row.studentId] || "Student";
    const label = variant === "negative" ? `-${row.points}` : `+${row.points}`;
    return (
      <div key={row.studentId} style={styles.eventRow}>
        <span style={styles.eventName}>{getFirstName(studentName)}</span>
        <span style={variant === "positive" ? styles.positiveTag : styles.negativeTag}>
          {label}
        </span>
      </div>
    );
  };

  const lastUpdatedText = useMemo(() => {
    if (!lastUpdated) return "";
    return lastUpdated.toLocaleTimeString();
  }, [lastUpdated]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Class {className}</h1>
          <div style={styles.subtitle}>Student reminders</div>
        </div>
        <div style={styles.actions}>
          <button onClick={loadData} style={styles.secondaryButton} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button onClick={clearView} style={styles.secondaryButton}>
            Clear
          </button>
          <button onClick={toggleFullscreen} style={styles.secondaryButton}>
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
          <Link to={`/classes/${id}/seating?mode=teaching`} style={styles.linkButton}>
            Back to Teaching
          </Link>
        </div>
      </div>

      <div style={styles.statusRow}>
        <span>Auto refresh every 15s</span>
        {lastUpdatedText && <span>Last updated: {lastUpdatedText}</span>}
      </div>

      <div style={styles.columns}>
        <div style={{ ...styles.column, ...styles.columnPositive }}>
          <h2 style={styles.columnTitle}>Recent positives</h2>
          {positiveRows.length === 0 ? (
            <div style={styles.emptyState}>No recent positives</div>
          ) : (
            positiveRows.map((row) => renderRow(row, "positive"))
          )}
        </div>
        <div style={{ ...styles.column, ...styles.columnNegative }}>
          <h2 style={styles.columnTitle}>Recent negatives</h2>
          {negativeRows.length === 0 ? (
            <div style={styles.emptyState}>No recent negatives</div>
          ) : (
            negativeRows.map((row) => renderRow(row, "negative"))
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f6fb",
    padding: "24px"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "12px"
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 700
  },
  subtitle: {
    color: "#526176",
    marginTop: "4px"
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap"
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    color: "#5b667a",
    fontSize: "14px",
    marginBottom: "20px"
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "20px"
  },
  column: {
    background: "#fff",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(24, 35, 55, 0.08)"
  },
  columnPositive: {
    background: "#eff9f1"
  },
  columnNegative: {
    background: "#fdf1f1"
  },
  columnTitle: {
    margin: "0 0 12px",
    fontSize: "18px"
  },
  eventRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #edf0f6"
  },
  eventName: {
    fontSize: "72px",
    fontWeight: 800
  },
  positiveTag: {
    background: "#2e7d32",
    color: "#fff",
    borderRadius: "999px",
    padding: "10px 16px",
    fontWeight: 800,
    fontSize: "28px"
  },
  negativeTag: {
    background: "#c62828",
    color: "#fff",
    borderRadius: "999px",
    padding: "10px 16px",
    fontWeight: 800,
    fontSize: "28px"
  },
  emptyState: {
    color: "#6e7a90",
    padding: "12px 0"
  },
  secondaryButton: {
    background: "#fff",
    color: "#2c3f63",
    border: "1px solid #cbd5e1",
    padding: "8px 14px",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer"
  },
  linkButton: {
    background: "#2c5fd3",
    color: "#fff",
    borderRadius: "8px",
    padding: "8px 14px",
    textDecoration: "none",
    fontWeight: 600
  }
};
